import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SettingsBloc } from '@kbloc';
import { EnvironmentVariablesService } from '@kenv';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { CardBloc } from '@kp/shared/components/cards/card.bloc';
import { UserBloc } from '@kp/user/user.bloc';
import { BrowserStorage, DataStoreService } from '@kservice';
import { Product } from '@ktypes/enums';
import { UserIdleService } from 'angular-user-idle';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GlobalElementsService {
  // Note: these arrays support regex matching for partial matching. For example,
  // '/welcome' will match all routes that contain /welcome (including
  // /welcome/privacy, /welcome/error, etc).
  routesToHideAllChrome = [
    '/auth',
    '/login(?!/(existing_account|reset))',
    '/welcome/signup$',
    '/welcome$',
    '/welcome#.*',
    '/initial.*',
  ];
  routesToHideHeader = [];
  routesToHideNav = ['/dialogue', '/pulse_survey', '/report/.*', '/welcome/.*'];
  routesToShowBasicLogoHeader = [
    '/access-expired',
    '/data-link/error',
    '/delete',
    '/dialogue',
    '/login/existing_account',
    '/privacy-full',
    '/privacy-pledge',
    '/pulse_survey',
    '/question_set',
    '/question',
    '/report/.*',
    '/resources',
    '/thank_you',
    '/unsubscribe',
    '/welcome/.*',
    'welcome/error',
    '/login/reset/.*',
  ];
  routesToShowBasicLogoHeaderWithIcons = ['/dialogue', '/privacy-pledge', '/pulse_survey', '/report/.*', '/resources'];
  routesToShowFullHeaderToResourcefulUsers = ['/report', '/resources'];
  routesToShowAccountCreationBanner = ['/resources'];
  routesToHideLiveSupport = ['/login', '/welcome/error'];

  private _subscriptions: Subscription[] = [];

  private _showChrome = new BehaviorSubject<boolean>(false);
  get showChrome$(): Observable<boolean> {
    return this._showChrome.asObservable();
  }

  private _showHeader = new BehaviorSubject<boolean>(false);
  get showHeader$(): Observable<boolean> {
    return this._showHeader.asObservable();
  }

  private _showNav = new BehaviorSubject<boolean>(false);
  get showNav$(): Observable<boolean> {
    return this._showNav.asObservable();
  }

  private _showBasicLogoHeader = new BehaviorSubject<boolean>(false);
  get showBasicLogoHeader$(): Observable<boolean> {
    return this._showBasicLogoHeader.asObservable();
  }

  private _showBasicLogoHeaderWithIcons = new BehaviorSubject<boolean>(false);
  get showBasicLogoHeaderWithIcons$(): Observable<boolean> {
    return this._showBasicLogoHeaderWithIcons.asObservable();
  }

  private _showAccountCreationBanner$ = new BehaviorSubject<boolean>(false);
  get showAccountCreationBanner$(): Observable<boolean> {
    return this._showAccountCreationBanner$.asObservable();
  }

  private _showLiveSupport$ = new BehaviorSubject<boolean>(false);

  get showLiveSupport$(): Observable<boolean> {
    return combineLatest([this._showLiveSupport$.asObservable(), this._userBloc.liveSupport$]).pipe(
      map(([showLiveSupport, liveSupport]) => {
        return showLiveSupport && liveSupport != null;
      })
    );
  }

  constructor(
    private _authenticationBloc: AuthenticationBloc,
    private _browserStorage: BrowserStorage,
    private _cardBloc: CardBloc,
    private _dataStoreService: DataStoreService,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _router: Router,
    private _settingsBloc: SettingsBloc,
    private _userBloc: UserBloc,
    private _userIdleService: UserIdleService
  ) {
    // Check routes on route changes and show/hide elements per route
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((navEnd: NavigationEnd) => {
      this._routeCheck(navEnd.urlAfterRedirects);
    });
  }

  watchIdleUser(isTodayPage?: boolean) {
    // only enable Idle User watcher for Purposeful for now
    if (this._authenticationBloc.isLoggedIn() && this._environmentVariablesService.product === Product.purposeful) {
      const stayLoggedIn = this._dataStoreService.stayLoggedIn;
      //Start watching for user inactivity.
      this._userIdleService.startWatching();
      const timerStartSub = this._userIdleService.onTimerStart().subscribe();
      const timeoutSub = this._userIdleService.onTimeout().subscribe(() => {
        if (stayLoggedIn) {
          this._pulseSurveyBloc.allowPulseSurveyCheckToday(true);
          this._userIdleService.resetTimer();
          if (isTodayPage === true) {
            return (window.location.href = '');
          }
          return this._router.navigate(['']);
        } else {
          this._authenticationBloc.logout();
        }
      });
      this._subscriptions.push(timerStartSub, timeoutSub);
    }
  }

  stopWatchingIdleUser(): void {
    this._subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private _isResourcefulUser(): boolean {
    return this._environmentVariablesService.product === Product.resourceful && this._authenticationBloc.isLoggedIn();
  }

  private _routeCheck(url: string) {
    const urlHasMatch = createUrlHasMatch(url);

    const showBasicLogoHeader =
      urlHasMatch(this.routesToShowBasicLogoHeader) &&
      !urlHasMatch(this.routesToHideAllChrome) &&
      !(this._isResourcefulUser() && urlHasMatch(this.routesToShowFullHeaderToResourcefulUsers));
    const showBasicLogoHeaderWithIcons =
      urlHasMatch(this.routesToShowBasicLogoHeaderWithIcons) && !urlHasMatch(this.routesToHideAllChrome);
    const showChrome = !showBasicLogoHeader && !urlHasMatch(this.routesToHideAllChrome);
    const showHeader =
      (showChrome && !urlHasMatch(this.routesToHideHeader)) ||
      (this._isResourcefulUser() && urlHasMatch(this.routesToShowFullHeaderToResourcefulUsers));
    const showNav =
      (showChrome && !urlHasMatch(this.routesToHideNav)) ||
      (this._isResourcefulUser() && urlHasMatch(this.routesToShowFullHeaderToResourcefulUsers));
    const showAccountCreationBanner =
      urlHasMatch(this.routesToShowAccountCreationBanner) && !this._authenticationBloc.isLoggedIn();
    const showLiveSupport = !urlHasMatch(this.routesToHideLiveSupport);

    this._showHeader.next(showHeader);
    this._showNav.next(showNav);
    this._showChrome.next(showChrome);
    this._showBasicLogoHeader.next(showBasicLogoHeader);
    this._showBasicLogoHeaderWithIcons.next(showBasicLogoHeaderWithIcons);
    this._showAccountCreationBanner$.next(showAccountCreationBanner);
    this._showLiveSupport$.next(showLiveSupport);
    this._cardBloc.detailViewClicked.next({ detailViewClicked: false, card: null, screen: null });

    // Reset scroll position when navigating (helps on mobile when screens get tall)
    window.scrollTo(0, 0);
  }
}

function createUrlHasMatch(url: string) {
  return (routes: string[]) => routes.some((route) => url.match(RegExp(route)));
}
